import React, { useEffect, useState } from "react";
import { getApiUrl } from "../../utils/api";
import "./FeedbackMessages.css";

function FeedbackMessages() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    isBulk: false,
    item: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const questionLabels = {
    q1: "Q1. How was your experience on the Home Page?",
    q2: "Q2. Was the Home Page layout clear and easy to navigate?",
    q3: "Q3. Did the About Page provide sufficient details about skills and background?",
    q4: "Q4. Was the content structure on the About Page logical?",
    q5: "Q5. How visually appealing was the design of the About Page?",
    q6: "Q6. Were the projects relevant and well-documented?",
    q7: "Q7. Did the project detail pages give enough technical context?",
    q8: "Q8. Were the internship experiences presented clearly?",
    q9: "Q9. Did the internship roles demonstrate practical engineering skills?",
    q10: "Q10. Were the blog posts informative and easy to read?",
    q11: "Q11. Did the blogs add value to understanding software engineering topics?",
    q12: "Q12. Would you recommend reading these technical articles?",
    q13: "Q13. Were the technical skills clearly categorized and accurate?",
    q14: "Q14. Did the skill ratings reflect real competence?",
    q15: "Q15. Were the certificates verified and easy to view?",
    q16: "Q16. Was the credentials presentation professional?",
    q17: "Q17. Was contacting or reaching out easy and seamless?",
    queries: "💬 Additional Queries / Suggestions:"
  };

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(getApiUrl("/admin/feedbacks"), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setFeedbacks(data.data);
      }
    } catch (err) {
      console.error("Error fetching feedback messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const filteredFeedbacks = feedbacks.filter((fb) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (fb.name && fb.name.toLowerCase().includes(q)) ||
      (fb.email && fb.email.toLowerCase().includes(q))
    );
  });

  const openDeleteSingleModal = (fb) => {
    setDeleteModal({
      isOpen: true,
      isBulk: false,
      item: fb
    });
  };

  const openDeleteAllModal = () => {
    setDeleteModal({
      isOpen: true,
      isBulk: true,
      item: null
    });
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setDeleteModal({
      isOpen: false,
      isBulk: false,
      item: null
    });
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("adminToken");
      let url = "";

      if (deleteModal.isBulk) {
        url = getApiUrl("/admin/feedbacks");
      } else {
        url = getApiUrl(`/admin/feedbacks/${deleteModal.item?._id}`);
      }

      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();
      if (data.success) {
        setDeleteModal({ isOpen: false, isBulk: false, item: null });
        await fetchFeedbacks();
      } else {
        alert(data.message || "Failed to delete feedback");
      }
    } catch (err) {
      console.error("Error deleting feedback:", err);
      alert("Error occurred while deleting. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="feedback-messages-container">
      <div className="page-header-row">
        <div>
          <h2>💬 Feedback Submissions</h2>
          <p className="page-sub">Comprehensive user feedback, ratings, and recommendations</p>
        </div>

        <div className="header-actions">
          <button className="btn-refresh" onClick={fetchFeedbacks} disabled={loading}>
            🔄 Refresh
          </button>
          <button
            className="btn-delete-all"
            onClick={openDeleteAllModal}
            disabled={loading || feedbacks.length === 0}
            title="Delete all feedback submissions"
          >
            🗑️ Delete All
          </button>
        </div>
      </div>

      {/* SEARCH BAR */}
      <div className="table-controls-row">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search feedback by user name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* FEEDBACK TABLE */}
      <div className="dash-glass-panel full-width-panel">
        {loading ? (
          <div className="loading-spinner-box">
            <div className="spinner"></div>
            <p>Loading feedback submissions...</p>
          </div>
        ) : filteredFeedbacks.length > 0 ? (
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date & Time</th>
                  <th>User Name</th>
                  <th>Email</th>
                  <th>Queries Summary</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeedbacks.map((fb, idx) => (
                  <tr key={fb._id || idx}>
                    <td>{idx + 1}</td>
                    <td>{new Date(fb.createdAt).toLocaleString()}</td>
                    <td className="user-name-cell">{fb.name}</td>
                    <td>{fb.email}</td>
                    <td className="queries-summary-cell">
                      {fb.queries ? (fb.queries.length > 40 ? `${fb.queries.substring(0, 40)}...` : fb.queries) : "N/A"}
                    </td>
                    <td>
                      <div className="action-buttons-group">
                        <button
                          className="btn-action-view"
                          onClick={() => setSelectedFeedback(fb)}
                        >
                          View Details 👁️
                        </button>
                        <button
                          className="btn-action-delete"
                          onClick={() => openDeleteSingleModal(fb)}
                        >
                          Delete 🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>No feedback submissions found.</p>
          </div>
        )}
      </div>

      {/* FEEDBACK DETAILS MODAL */}
      {selectedFeedback && (
        <div className="feedback-modal-overlay" onClick={() => setSelectedFeedback(null)}>
          <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <div>
                <h2>📝 Complete Feedback Details</h2>
                <p className="modal-sub">
                  Submitted by <b style={{ color: "#38bdf8" }}>{selectedFeedback.name}</b> ({selectedFeedback.email}) on {new Date(selectedFeedback.createdAt).toLocaleString()}
                </p>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedFeedback(null)}>
                ✖
              </button>
            </div>

            <div className="modal-body-answers">
              {Object.keys(questionLabels).map((qKey) => (
                <div key={qKey} className={`qa-card ${qKey === "queries" ? "queries-qa-card" : ""}`}>
                  <div className="qa-question">{questionLabels[qKey]}</div>
                  <div className="qa-answer">{selectedFeedback[qKey] || "N/A"}</div>
                </div>
              ))}
            </div>

            <div className="modal-footer-row">
              <button className="btn-close-modal" onClick={() => setSelectedFeedback(null)}>
                Close Window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WARNING DELETE MODAL */}
      {deleteModal.isOpen && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="warning-icon-badge">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>

            <h3 className="delete-modal-title">Are you sure want to Delete</h3>

            <p className="delete-modal-text">
              {deleteModal.isBulk
                ? "Are you sure you want to delete all feedback submissions? This action cannot be undone."
                : `Are you sure you want to delete feedback from "${deleteModal.item?.name}" (${deleteModal.item?.email})?`}
            </p>

            <div className="delete-modal-actions">
              <button
                className="btn-modal-cancel"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn-modal-delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackMessages;
